import React, { useEffect, useMemo, useRef, useState } from 'react'

const INSTANCES_PER_EVENT = 6
const MAX_ACTIVE_INSTANCES = 150

const EmojiRainOverlay = ({ events = [] }) => {
  const [activeEmojis, setActiveEmojis] = useState([])
  const processedIdsRef = useRef(new Set())
  const removalTimersRef = useRef(new Map())

  useEffect(() => {
    return () => {
      removalTimersRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId)
      })
      removalTimersRef.current.clear()
    }
  }, [])

  useEffect(() => {
    if (!Array.isArray(events) || events.length === 0) {
      return
    }

    const newEvents = events.filter((event) => {
      if (!event || !event.id) return false
      return !processedIdsRef.current.has(event.id)
    })

    if (newEvents.length === 0) {
      return
    }

    const additions = []

    newEvents.forEach((event) => {
      processedIdsRef.current.add(event.id)

      const count = Number.isFinite(event.count) && event.count > 0
        ? Math.min(Math.floor(event.count), 30)
        : INSTANCES_PER_EVENT

      for (let index = 0; index < count; index += 1) {
        const instanceId = `${event.id}-${index}-${Math.random().toString(16).slice(2, 8)}`
        additions.push({
          instanceId,
          emoji: typeof event.emoji === 'string' && event.emoji.trim() ? event.emoji : '👏🏼',
          left: 10 + Math.random() * 80,
          delay: Math.random() * 400,
          duration: 2000 + Math.random() * 800,
          size: 2.4 + Math.random() * 0.8
        })
      }
    })

    if (additions.length === 0) {
      return
    }

    setActiveEmojis((prev) => {
      const merged = [...prev, ...additions]
      if (merged.length > MAX_ACTIVE_INSTANCES) {
        return merged.slice(merged.length - MAX_ACTIVE_INSTANCES)
      }
      return merged
    })

    additions.forEach((instance) => {
      const lifetime = instance.duration + instance.delay + 120
      const timeoutId = window.setTimeout(() => {
        setActiveEmojis((prev) => prev.filter((item) => item.instanceId !== instance.instanceId))
        removalTimersRef.current.delete(instance.instanceId)
      }, lifetime)
      removalTimersRef.current.set(instance.instanceId, timeoutId)
    })
  }, [events])

  const overlayContent = useMemo(() => {
    if (activeEmojis.length === 0) {
      return null
    }

    return activeEmojis.map((instance) => (
      <span
        key={instance.instanceId}
        className="emoji-rain-item"
        style={{
          left: `${instance.left}%`,
          animationDuration: `${instance.duration}ms`,
          animationDelay: `${instance.delay}ms`,
          fontSize: `${instance.size}rem`
        }}
      >
        {instance.emoji}
      </span>
    ))
  }, [activeEmojis])

  if (!overlayContent) {
    return null
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-visible">
      {overlayContent}
    </div>
  )
}

export default EmojiRainOverlay
